import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';
import { ArrowLeft, Calendar, ExternalLink, Eye, FileText, CheckCircle, Clock, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

interface RssItem {
  id: string;
  title: string;
  description?: string;
  link: string;
  pub_date?: string;
  author?: string;
  category?: string;
  status: string;
  task_id?: string;
  processed_at?: string;
  created_at: string;
  scrape_tasks?: {
    id: string;
    name: string;
    status: string;
  };
}

interface RssSource {
  id: string;
  name: string;
  rss_url: string;
}

interface RssItemManagementProps {
  sourceId: string;
  sourceName: string;
  onBack: () => void;
}

const RssItemManagement: React.FC<RssItemManagementProps> = ({ sourceId, sourceName, onBack }) => {
  const [items, setItems] = useState<RssItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const statusInfo = {
    pending: { label: '待处理', icon: Clock, color: 'yellow' },
    processed: { label: '已处理', icon: CheckCircle, color: 'green' },
    task_created: { label: '已创建任务', icon: CheckCircle, color: 'blue' },
    failed: { label: '处理失败', icon: AlertCircle, color: 'red' },
    skipped: { label: '已跳过', icon: XCircle, color: 'gray' }
  };

  useEffect(() => {
    fetchItems();
  }, [sourceId, currentPage, pageSize]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_CONFIG.FUNCTIONS_URL}/scrape-admin-manage?action=rss_items&source_id=${sourceId}&page=${currentPage}&limit=${pageSize}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setItems(result.data.data || []);
        setTotalCount(result.data.pagination?.total || 0);
        setTotalPages(result.data.pagination?.totalPages || 0);
        setCurrentPage(result.data.pagination?.page || 1);
      } else {
        console.error('获取RSS条目失败:', result.error);
        toast.error('获取RSS条目失败');
      }
    } catch (error) {
      console.error('获取RSS条目失败:', error);
      toast.error('获取RSS条目失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const getStatusInfo = (status: string) => {
    return statusInfo[status as keyof typeof statusInfo] || statusInfo.pending;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未知';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                返回
              </button>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <FileText className="w-6 h-6 mr-2 text-blue-600" />
                  RSS条目管理
                </h2>
                <p className="text-sm text-gray-600 mt-1">RSS源: {sourceName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchItems}
                disabled={loading}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                title="刷新RSS条目列表"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* 统计信息 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-600">总条目数</p>
                  <p className="text-2xl font-bold text-blue-900">{totalCount}</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Clock className="w-8 h-8 text-yellow-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-yellow-600">待处理</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {items.filter(item => item.status === 'pending').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-600">已处理</p>
                  <p className="text-2xl font-bold text-green-900">
                    {items.filter(item => item.status === 'processed' || item.status === 'task_created').length}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-600">处理失败</p>
                  <p className="text-2xl font-bold text-red-900">
                    {items.filter(item => item.status === 'failed').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RSS条目列表 */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    条目信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    发布信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    处理状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    关联任务
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => {
                  const status = getStatusInfo(item.status);
                  const StatusIcon = status.icon;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      {/* 条目信息 */}
                      <td className="px-6 py-4">
                        <div className="max-w-[300px]">
                          <div className="text-sm font-medium text-gray-900 mb-1 truncate" title={item.title}>
                            {item.title}
                          </div>
                          {item.description && (
                            <div className="text-sm text-gray-500 mb-2 truncate" title={item.description}>
                              {item.description}
                            </div>
                          )}
                          <div className="text-xs text-blue-600 flex items-center">
                            <ExternalLink className="w-3 h-3 mr-1 flex-shrink-0" />
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline truncate max-w-[250px]"
                              title={item.link}
                            >
                              {item.link}
                            </a>
                          </div>
                        </div>
                      </td>
                      
                      {/* 发布信息 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                            {formatDate(item.pub_date)}
                          </div>
                          {item.author && (
                            <div className="text-xs text-gray-500">
                              作者: {item.author}
                            </div>
                          )}
                          {item.category && (
                            <div className="text-xs text-gray-500">
                              分类: {item.category}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* 处理状态 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </span>
                          {item.processed_at && (
                            <div className="text-xs text-gray-500">
                              处理时间: {formatDate(item.processed_at)}
                            </div>
                          )}
                        </div>
                      </td>
                      
                      {/* 关联任务 */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.scrape_tasks ? (
                          <div className="text-sm max-w-[200px]">
                            <div className="font-medium text-gray-900 truncate" title={item.scrape_tasks.name}>
                              {item.scrape_tasks.name}
                            </div>
                            <div className={`text-xs truncate ${
                              item.scrape_tasks.status === 'completed' ? 'text-green-600' :
                              item.scrape_tasks.status === 'running' ? 'text-blue-600' :
                              item.scrape_tasks.status === 'failed' ? 'text-red-600' :
                              'text-gray-500'
                            }`} title={item.scrape_tasks.status}>
                              {item.scrape_tasks.status}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">无关联任务</span>
                        )}
                      </td>
                      
                      {/* 操作 */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900"
                            title="查看原文"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            title="在新窗口打开"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {items.length === 0 && !loading && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无RSS条目数据</p>
            </div>
          )}

          {/* 分页组件 */}
          {totalCount > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 mt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <span>每页显示</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>条，共 {totalCount} 条</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    首页
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  
                  {/* 页码按钮 */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 text-sm border rounded ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    末页
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RssItemManagement;